import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '@/components/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MobileBar from '@/components/feature/MobileBar';
import { whatsappLink, instagram, email, address, gymInfo } from '@/mocks/gymInfo';

export default function Contato() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', phone: '', goal: '' });
  const [sent, setSent] = useState(false);

  const title = t('seo.contato.title');
  const description = t('seo.contato.description');
  const keywords = t('seo.contato.keywords');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://topgym.com.br';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: title,
    description: description,
    url: `${siteUrl}/contato`,
    mainEntity: {
      '@type': 'GymOrSportsActivityLocation',
      name: 'Top Gym Cumbuco',
      telephone: '+55-85-98236-1153',
      email: 'topgymcumbuco@gmail.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Av. Central, 2211 - Praia de Cumbuco',
        addressLocality: 'Cumbuco',
        addressRegion: 'CE',
        postalCode: '61619-270',
        addressCountry: 'BR',
      },
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('nav.home'),
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('page.contato.title'),
        item: `${siteUrl}/contato`,
      },
    ],
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name.trim() && formData.phone.trim()) {
      const message = [
        `${t('page.contato.name')}: ${formData.name.trim()}`,
        `${t('page.contato.phone')}: ${formData.phone.trim()}`,
        formData.goal.trim() ? `${t('page.contato.goal')}: ${formData.goal.trim()}` : '',
      ]
        .filter(Boolean)
        .join('\n');
      window.open(`${whatsappLink}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
      setSent(true);
      setFormData({ name: '', phone: '', goal: '' });
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SEO title={title} description={description} keywords={keywords} path="/contato" schema={[schema, breadcrumbSchema]} />
      <Navbar />
      <main className="overflow-x-hidden pb-20 md:pb-0">
        <section className="bg-foreground-950 py-20 md:py-28">
          <div className="w-full px-4 md:px-6">
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-background-50">
              {t('page.contato.title')}
            </h1>
            <p className="font-body text-base text-background-300 mt-4 max-w-xl">
              {t('page.contato.subtitle')}
            </p>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              <div className="space-y-8">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6">{t('page.contato.contactUs')}</h2>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center text-primary-500 flex-shrink-0">
                        <i className="ri-whatsapp-line text-xl"></i>
                      </div>
                      <div>
                        <p className="font-body text-sm text-foreground-500">{t('page.contato.whatsapp')}</p>
                        <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="font-body text-base text-foreground-950 hover:text-primary-600 transition-colors">{gymInfo.phone}</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center text-primary-500 flex-shrink-0">
                        <i className="ri-mail-line text-xl"></i>
                      </div>
                      <div>
                        <p className="font-body text-sm text-foreground-500">{t('page.contato.email')}</p>
                        <a href={`mailto:${email}`} className="font-body text-base text-foreground-950 hover:text-primary-600 transition-colors">{email}</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center text-primary-500 flex-shrink-0">
                        <i className="ri-map-pin-line text-xl"></i>
                      </div>
                      <div>
                        <p className="font-body text-sm text-foreground-500">{t('page.contato.address')}</p>
                        <p className="font-body text-base text-foreground-950">{address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center text-primary-500 flex-shrink-0">
                        <i className="ri-instagram-line text-xl"></i>
                      </div>
                      <div>
                        <p className="font-body text-sm text-foreground-500">{t('page.contato.instagram')}</p>
                        <a href={instagram} target="_blank" rel="noopener noreferrer" className="font-body text-base text-foreground-950 hover:text-primary-600 transition-colors">@topgymcumbuco</a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200 flex flex-col items-center text-center">
                  <h3 className="font-heading text-lg text-foreground-950 mb-1">{t('page.contato.qrTitle')}</h3>
                  <p className="font-body text-xs text-foreground-500 mb-4">{t('page.contato.qrSubtitle')}</p>
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(whatsappLink)}`}
                      alt="QR Code WhatsApp Top Gym Cumbuco"
                      title="QR Code WhatsApp Top Gym Cumbuco"
                      className="w-36 h-36 md:w-44 md:h-44 rounded-lg"
                      width="176"
                      height="176"
                    />
                  </a>
                  <p className="font-body text-xs text-foreground-500 mt-3">{gymInfo.phone}</p>
                </div>

                <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200">
                  <h3 className="font-heading text-xl text-foreground-950 mb-3">{t('page.contato.hours')}</h3>
                  <div className="space-y-1 font-body text-sm text-foreground-700">
                    <p><span className="text-foreground-950 font-medium">{t('page.contato.weekday')}</span> {t('home.schedule.weekdayHours')}</p>
                    <p><span className="text-foreground-950 font-medium">{t('page.contato.saturday')}</span> {t('home.schedule.saturdayHours')}</p>
                    <p><span className="text-foreground-950 font-medium">{t('page.contato.sunday')}</span> {t('home.schedule.sundayHours')}</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">{t('page.contato.formTitle')}</h2>
                <p className="font-body text-sm text-foreground-600 mb-6">{t('page.contato.formSubtitle')}</p>
                {sent ? (
                  <div className="bg-primary-100 rounded-lg p-5 text-center">
                    <i className="ri-check-line text-3xl text-primary-600 mb-2"></i>
                    <p className="font-body text-base text-foreground-950 font-medium">{t('page.contato.sentTitle')}</p>
                    <p className="font-body text-sm text-foreground-600 mt-1">{t('page.contato.sentText')}</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block font-body text-sm text-foreground-700 mb-1">{t('page.contato.name')}</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-lg font-body text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder={t('page.contato.namePlaceholder')}
                        required
                      />
                    </div>
                    <div>
                      <label className="block font-body text-sm text-foreground-700 mb-1">{t('page.contato.phone')}</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-lg font-body text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder={t('page.contato.phonePlaceholder')}
                        required
                      />
                    </div>
                    <div>
                      <label className="block font-body text-sm text-foreground-700 mb-1">{t('page.contato.goal')}</label>
                      <textarea
                        value={formData.goal}
                        onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                        className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-lg font-body text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                        placeholder={t('page.contato.goalPlaceholder')}
                        rows={4}
                        maxLength={500}
                      />
                      <p className="text-xs text-foreground-500 mt-1 text-right">{formData.goal.length}/500</p>
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-primary-500 hover:bg-primary-600 text-foreground-950 font-body text-sm font-semibold px-5 py-3 rounded-full transition-colors"
                    >
                      {t('page.contato.submit')}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <MobileBar />
    </div>
  );
}